// src/pages/AdminProfile.jsx
import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  User,
  Lock,
  Edit2,
  Save,
  X,
  CheckCircle,
  AlertCircle,
  Eye,
  EyeOff,
  Loader2
} from 'lucide-react';
import adminProfileService from '../services/adminProfileService';
import { adminAuthService } from '../services/adminAuthService';

const TIMEZONES = [
  'UTC', 'America/New_York', 'America/Chicago', 'America/Denver',
  'America/Los_Angeles', 'Europe/London', 'Europe/Paris', 'Europe/Berlin',
  'Asia/Dubai', 'Asia/Kolkata', 'Asia/Singapore', 'Asia/Tokyo',
  'Australia/Sydney', 'Pacific/Auckland'
];

const AdminProfile = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = searchParams.get('tab') || 'profile';

  // ── Profile tab state ──────────────────────────────────
  const [profile, setProfile]       = useState(null);
  const [loadingProfile, setLoadingProfile] = useState(true);
  const [editing, setEditing]       = useState(false);
  const [editForm, setEditForm]     = useState({});
  const [saving, setSaving]         = useState(false);
  const [profileMsg, setProfileMsg] = useState(null); // { type: 'success'|'error', text }

  // ── Security tab state ─────────────────────────────────
  const [pwForm, setPwForm]   = useState({ currentPassword: '', newPassword: '', confirmPassword: '' });
  const [pwSaving, setPwSaving] = useState(false);
  const [pwMsg, setPwMsg]     = useState(null); // { type: 'success'|'error', text }
  const [showPw, setShowPw]   = useState({ current: false, next: false, confirm: false });

  // ── Load profile on mount ──────────────────────────────
  useEffect(() => {
    adminProfileService.getProfile()
      .then(data => {
        setProfile(data);
        setEditForm({
          firstName: data.firstName || '',
          lastName:  data.lastName  || '',
          phone:     data.phone     || '',
          jobTitle:  data.jobTitle  || '',
          bio:       data.bio       || '',
          timezone:  data.timezone  || 'UTC',
        });
      })
      .catch(() => setProfileMsg({ type: 'error', text: 'Failed to load profile.' }))
      .finally(() => setLoadingProfile(false));
  }, []);

  // ── Tab switch helper ──────────────────────────────────
  const switchTab = (tab) => {
    setSearchParams({ tab });
    setProfileMsg(null);
    setPwMsg(null);
  };

  // ── Profile save ───────────────────────────────────────
  const handleProfileSave = async () => {
    setSaving(true);
    setProfileMsg(null);
    try {
      const updated = await adminProfileService.updateProfile(editForm);
      setProfile(updated);
      setEditForm({
        firstName: updated.firstName || '',
        lastName:  updated.lastName  || '',
        phone:     updated.phone     || '',
        jobTitle:  updated.jobTitle  || '',
        bio:       updated.bio       || '',
        timezone:  updated.timezone  || 'UTC',
      });
      // Refresh localStorage so sidebar name updates
      const stored = adminAuthService.getAdminUser();
      if (stored) {
        localStorage.setItem('adminUser', JSON.stringify({
          ...stored,
          firstName: updated.firstName,
          lastName:  updated.lastName,
          email:     updated.email,
        }));
      }
      setEditing(false);
      setProfileMsg({ type: 'success', text: 'Profile updated successfully.' });
    } catch (err) {
      setProfileMsg({ type: 'error', text: err.message || 'Failed to update profile.' });
    } finally {
      setSaving(false);
    }
  };

  const handleEditCancel = () => {
    setEditing(false);
    setEditForm({
      firstName: profile?.firstName || '',
      lastName:  profile?.lastName  || '',
      phone:     profile?.phone     || '',
      jobTitle:  profile?.jobTitle  || '',
      bio:       profile?.bio       || '',
      timezone:  profile?.timezone  || 'UTC',
    });
    setProfileMsg(null);
  };

  // ── Password change ────────────────────────────────────
  const handlePasswordChange = async (e) => {
    e.preventDefault();
    setPwMsg(null);

    if (pwForm.newPassword.length < 8) {
      setPwMsg({ type: 'error', text: 'New password must be at least 8 characters.' });
      return;
    }
    if (pwForm.newPassword !== pwForm.confirmPassword) {
      setPwMsg({ type: 'error', text: 'New password and confirmation do not match.' });
      return;
    }

    setPwSaving(true);
    try {
      await adminProfileService.changePassword({
        currentPassword: pwForm.currentPassword,
        newPassword:     pwForm.newPassword,
      });
      setPwForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
      setShowPw({ current: false, next: false, confirm: false });
      setPwMsg({ type: 'success', text: 'Password changed successfully.' });
    } catch (err) {
      setPwMsg({ type: 'error', text: err.message || 'Failed to change password.' });
    } finally {
      setPwSaving(false);
    }
  };

  // ── Shared helpers ─────────────────────────────────────
  const initials = profile
    ? `${profile.firstName?.charAt(0) || ''}${profile.lastName?.charAt(0) || ''}`
    : '??';

  const roleLabel = (role) => {
    if (role === 'PLATFORM_OWNER') return 'Platform Owner';
    if (role === 'PLATFORM_ADMIN') return 'Platform Admin';
    return role || '—';
  };

  const Alert = ({ msg }) => {
    if (!msg) return null;
    const isSuccess = msg.type === 'success';
    return (
      <div className={`flex items-center gap-2 p-3 rounded-lg text-sm ${
        isSuccess ? 'bg-green-50 text-green-700 border border-green-200'
                  : 'bg-red-50 text-red-700 border border-red-200'
      }`}>
        {isSuccess
          ? <CheckCircle className="w-4 h-4 flex-shrink-0" />
          : <AlertCircle className="w-4 h-4 flex-shrink-0" />}
        {msg.text}
      </div>
    );
  };

  const PasswordField = ({ label, field, showKey }) => (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
      <div className="relative">
        <input
          type={showPw[showKey] ? 'text' : 'password'}
          value={pwForm[field]}
          onChange={e => setPwForm(p => ({ ...p, [field]: e.target.value }))}
          className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
          required
        />
        <button
          type="button"
          onClick={() => setShowPw(p => ({ ...p, [showKey]: !p[showKey] }))}
          className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
        >
          {showPw[showKey] ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
        </button>
      </div>
    </div>
  );

  // ── Render ─────────────────────────────────────────────
  return (
    <div className="max-w-3xl mx-auto space-y-6">
      {/* Page header */}
      <div>
        <h1 className="text-3xl font-bold text-gray-900">My Account</h1>
        <p className="text-gray-600 mt-1">Manage your profile and security settings</p>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="flex gap-6">
          {[
            { key: 'profile',  label: 'Profile',  icon: User },
            { key: 'security', label: 'Security', icon: Lock },
          ].map(({ key, label, icon: Icon }) => (
            <button
              key={key}
              onClick={() => switchTab(key)}
              className={`flex items-center gap-2 pb-3 text-sm font-medium border-b-2 transition-colors ${
                activeTab === key
                  ? 'border-primary-600 text-primary-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              <Icon className="w-4 h-4" />
              {label}
            </button>
          ))}
        </nav>
      </div>

      {/* ── PROFILE TAB ── */}
      {activeTab === 'profile' && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 space-y-6">
          {loadingProfile ? (
            <div className="flex justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-primary-600" />
            </div>
          ) : (
            <>
              {/* Avatar + name row */}
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="w-16 h-16 bg-gradient-to-r from-primary-600 to-blue-600 rounded-full flex items-center justify-center shadow-md">
                    <span className="text-xl font-bold text-white">{initials}</span>
                  </div>
                  <div>
                    <h2 className="text-xl font-bold text-gray-900">{profile?.fullName}</h2>
                    <span className="inline-block mt-1 px-2 py-0.5 bg-primary-100 text-primary-700 text-xs font-medium rounded-full">
                      {roleLabel(profile?.platformRole)}
                    </span>
                  </div>
                </div>
                {!editing && (
                  <button
                    onClick={() => setEditing(true)}
                    className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors text-sm font-medium"
                  >
                    <Edit2 className="w-4 h-4" />
                    Edit Profile
                  </button>
                )}
              </div>

              <Alert msg={profileMsg} />

              {/* Fields */}
              {editing ? (
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">First Name</label>
                      <input
                        type="text"
                        value={editForm.firstName}
                        onChange={e => setEditForm(p => ({ ...p, firstName: e.target.value }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Last Name</label>
                      <input
                        type="text"
                        value={editForm.lastName}
                        onChange={e => setEditForm(p => ({ ...p, lastName: e.target.value }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Email <span className="text-gray-400 font-normal">(read-only)</span></label>
                    <input
                      type="email"
                      value={profile?.email || ''}
                      readOnly
                      className="w-full px-3 py-2 border border-gray-200 rounded-lg bg-gray-50 text-gray-500 cursor-not-allowed"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
                    <input
                      type="tel"
                      value={editForm.phone}
                      onChange={e => setEditForm(p => ({ ...p, phone: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Job Title</label>
                    <input
                      type="text"
                      value={editForm.jobTitle}
                      onChange={e => setEditForm(p => ({ ...p, jobTitle: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Bio</label>
                    <textarea
                      value={editForm.bio}
                      onChange={e => setEditForm(p => ({ ...p, bio: e.target.value }))}
                      rows={3}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent resize-none"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Timezone</label>
                    <select
                      value={editForm.timezone}
                      onChange={e => setEditForm(p => ({ ...p, timezone: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    >
                      {TIMEZONES.map(tz => (
                        <option key={tz} value={tz}>{tz}</option>
                      ))}
                    </select>
                  </div>

                  <div className="flex gap-3 pt-2">
                    <button
                      onClick={handleProfileSave}
                      disabled={saving}
                      className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-60 transition-colors text-sm font-medium"
                    >
                      {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                      {saving ? 'Saving…' : 'Save Changes'}
                    </button>
                    <button
                      onClick={handleEditCancel}
                      disabled={saving}
                      className="flex items-center gap-2 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 disabled:opacity-60 transition-colors text-sm font-medium"
                    >
                      <X className="w-4 h-4" />
                      Cancel
                    </button>
                  </div>
                </div>
              ) : (
                /* View mode */
                <div className="grid grid-cols-2 gap-x-8 gap-y-4">
                  {[
                    { label: 'Email',     value: profile?.email },
                    { label: 'Phone',     value: profile?.phone     || '—' },
                    { label: 'Job Title', value: profile?.jobTitle  || '—' },
                    { label: 'Timezone',  value: profile?.timezone  || '—' },
                    { label: 'Account Status', value: profile?.isActive ? 'Active' : 'Inactive' },
                    { label: 'Last Login', value: profile?.lastLogin
                        ? new Date(profile.lastLogin).toLocaleString() : '—' },
                  ].map(({ label, value }) => (
                    <div key={label}>
                      <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">{label}</p>
                      <p className="mt-1 text-sm text-gray-900">{value}</p>
                    </div>
                  ))}
                  {profile?.bio && (
                    <div className="col-span-2">
                      <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">Bio</p>
                      <p className="mt-1 text-sm text-gray-900">{profile.bio}</p>
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* ── SECURITY TAB ── */}
      {activeTab === 'security' && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-1">Change Password</h2>
          <p className="text-sm text-gray-500 mb-6">
            Choose a strong password of at least 8 characters.
          </p>

          <form onSubmit={handlePasswordChange} className="space-y-4 max-w-sm">
            <Alert msg={pwMsg} />

            <PasswordField label="Current Password"  field="currentPassword"  showKey="current" />
            <PasswordField label="New Password"       field="newPassword"      showKey="next"    />
            <PasswordField label="Confirm New Password" field="confirmPassword" showKey="confirm" />

            <button
              type="submit"
              disabled={pwSaving}
              className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-60 transition-colors text-sm font-medium"
            >
              {pwSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Lock className="w-4 h-4" />}
              {pwSaving ? 'Saving…' : 'Change Password'}
            </button>
          </form>
        </div>
      )}
    </div>
  );
};

export default AdminProfile;
